'use client';

import { FC, MouseEvent, memo, useMemo } from 'react';
import { localeDict } from '@/consts/languageAccordance';
import { Box, List, ListItem } from '@mui/material';
import { useRouter } from '@/hooks/general/useRouter/useRouter';
import { getFlagWithStyles } from '@/utils/locale/getFlag';
import { useAuthStore } from '@/store/authStore/authStore';
import { usePhrasesStore } from '@/store/phrasesStore';
import { updateLocale } from '@/utils/locale/updateLocale';
import { getLocale, getSortedLocales } from '@/utils/locale/language';
import { useThemeStore } from '@/store/themeStore';
import { useElementDimensionBySelector } from '@/hooks/helpers/useElementDimensionBySelector/useElementDimensionBySelector';
import { getMobileHeaderStyles } from '../menuUtils';
import { useCurrentPath } from '@/hooks/general/useCurrentPath/useCurrentPath';
import { useLocale } from '@/hooks/general/useLocale/useLocale';

interface MobileLangSwitcherProps {
    open: boolean;
    handleClose: () => void;
}

export const MobileLangSwitcher: FC<MobileLangSwitcherProps> = ({ open, handleClose }) => {
    const router = useRouter();
    const currentPath = useCurrentPath();
    const i18nLocale = useLocale();
    const { setLocale } = usePhrasesStore();
    const { variables } = useThemeStore();
    const isLoggedIn = useAuthStore((s) => s.loggedIn);

    const headerHeight = useElementDimensionBySelector({
        selector: 'header',
        dimension: 'height',
        observeResize: false,
    });
    const { listSX, listItemSX, activeListItemSX, boxItemSX } = useMemo(
        () => getMobileHeaderStyles(open, headerHeight),
        [open, headerHeight],
    );

    const handleMenuClick = async (evt: MouseEvent<HTMLLIElement>) => {
        await updateLocale({
            locale: evt.currentTarget.dataset.value,
            isLoggedIn,
            router,
            currentPath,
            i18nLocale,
            setLocale,
        });
        handleClose();
    };

    const getMenuItemText = (localeString: string) => {
        const locale = getLocale(localeString);
        const countryName = locale.country.localeName;
        const languageName = locale.language.localeName;
        return `${countryName} (${languageName})`;
    };

    const sortedLocales = getSortedLocales(
        isLoggedIn,
        i18nLocale.locales,
        i18nLocale.locale,
        variables?.sortOrder?.locale || [],
    );

    return (
        <Box sx={boxItemSX} className="mobileMenuBox">
            <nav>
                {open && (
                    <List
                        className="topNavigationMenu"
                        sx={listSX}
                        data-testid="mobile-lang-switcher"
                    >
                        {sortedLocales &&
                            sortedLocales.map((locale: keyof typeof localeDict) => {
                                const isActive = locale === i18nLocale.locale;
                                return (
                                    <ListItem
                                        key={`mobile-lang-menu-item-${locale}`}
                                        data-value={locale}
                                        onClick={handleMenuClick}
                                        sx={(theme) =>
                                            isActive ? activeListItemSX(theme) : listItemSX(theme)
                                        }
                                        className={`mobileLanguageItem ${isActive ? 'active' : ''}`}
                                    >
                                        {getFlagWithStyles(localeDict[locale].country.code)}
                                        <span data-value={locale}>{getMenuItemText(locale)}</span>
                                    </ListItem>
                                );
                            })}
                    </List>
                )}
            </nav>
        </Box>
    );
};

export const MemoizedMobileLangSwitcher = memo(MobileLangSwitcher);
